import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, getGridStringOperators } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { gamesService } from '../api/games';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import CustomPagination from 'components/common/pagination/pagination';
import { Link } from 'react-router-dom';
import { fetchSites } from 'store/sitesSlice';
import { LiveLog } from './LiveLog';
import styles from './games.module.scss';
import commonStyles from 'styles/common.module.scss';
import { RootState } from 'store';

const ActiveSessions: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );

  const [selectedSite, setSelectedSite] = useState<string | number>('');
  const [sessions, setSessions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [selectedSessionForLog, setSelectedSessionForLog] = useState<any>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await gamesService.getActiveSessions({
        page,
        limit,
        sortField: 'id',
        sortValue: 'DESC',
        siteId: selectedSite === 'all' ? undefined : selectedSite,
      });

      if (response?.data?.docs) {
        setSessions(response.data.docs);
        setTotal(response.data.totalDocs || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch active sessions:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to fetch active sessions',
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedSite, dispatch]);

  useEffect(() => {
    if (!sites || sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    } else if (sites.length > 0 && selectedSite === '') {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite, dispatch]);

  useEffect(() => {
    if (selectedSite !== '') {
      fetchSessions();
    }
  }, [fetchSessions, selectedSite]);

  const handleLiveLogClick = (row: any) => {
    setSelectedSessionForLog(row);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80, filterOperators: customStringOperators },
    {
      field: 'username',
      headerName: 'User',
      width: 110,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Link
          to={`/clients/${params.row.userId}`}
          style={{ color: 'var(--accent-blue)', textDecoration: 'none', fontWeight: 500 }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'gameTitle',
      headerName: 'Game',
      flex: 1,
      minWidth: 200,
      headerClassName: 'align-left-header',
      cellClassName: 'align-left-cell',
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Box className={styles.gameInfo} sx={{ width: '100%', justifyContent: 'flex-start' }}>
          {params.row.gameImageUrl && (
            <img
              src={params.row.gameImageUrl}
              alt={params.row.gameTitle}
              className={styles.gameThumb}
            />
          )}
          <Typography noWrap variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.row.gameTitle}
          </Typography>
        </Box>
      ),
    },
    { field: 'ip', headerName: 'IP Address', width: 150, filterOperators: customStringOperators },
    {
      field: 'clientType',
      headerName: 'Device',
      width: 120,
      filterOperators: customStringOperators,
    },
    // {
    //   field: 'lastActivityAt',
    //   headerName: 'Last Activity',
    //   width: 160,
    //   renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    // },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 160,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleLiveLogClick(params.row)}
          sx={{
            bgcolor: 'var(--accent-blue)',
            color: 'var(--text-primary)',
            '&:hover': { bgcolor: 'var(--accent-blue-hover)' },
          }}
        >
          Live Log
        </Button>
      ),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
      rowsPerPageOptions={[10, 20, 50, 100]}
    />
  );

  if (selectedSessionForLog) {
    return (
      <LiveLog
        session={selectedSessionForLog}
        siteId={selectedSite}
        onBack={() => {
          setSelectedSessionForLog(null);
          fetchSessions();
        }}
      />
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Active Sessions
        </Typography>

        <Box className={styles.filters}>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={selectedSite}
                label="Site"
                onChange={(e) => {
                  setSelectedSite(e.target.value as string | number);
                  setPage(1);
                }}
                className={styles.select}
                disabled={sitesLoading}
              >
                <MenuItem value="all">All Sites</MenuItem>
                {sites?.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={sessions}
          rowCount={total}
          paginationMode="server"
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          sx={{
            ...commonGridStyles,
            '& .align-left-header .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start !important',
            },
            '& .align-left-cell': {
              justifyContent: 'flex-start !important',
            },
          }}
          disableColumnMenu
          autoHeight
          hideFooterPagination
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default ActiveSessions;
